import { useEffect, useState, useRef } from "react";
import {
  Loader2,
  Pencil,
  ImagePlus,
  FileText,
  Trash2,
  BadgeCheck,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CUISINES } from "@/lib/cuisines";
import { track, EVENTS } from "@/lib/analytics";

/**
 * ClaimCard — one per verified claim. Header shows the listing + verified
 * badge state + "Edit listing" toggle. Expanding reveals the EditPanel.
 *
 * The big "Verify your Halal Status here" green CTA panel (shown when the
 * listing doesn't yet have a paid halal-verified badge) is the entry point
 * we added in iter15 — it surfaces the $5/yr badge with explainer copy so
 * owners understand what they're paying for.
 */
export default function ClaimCard({ claim, token, onChanged }) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(null);
  const [loading, setLoading] = useState(false);

  const loadDraft = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(
        `/owner/restaurants/${claim.place_id}/draft`,
        { headers: { "X-Owner-Token": token } }
      );
      setDraft(data);
    } catch (err) {
      console.warn("[claim-card] draft load failed", err?.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && !draft) loadDraft();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <div
      data-testid={`owner-claim-${claim.place_id}`}
      className={`rounded-2xl border p-4 bg-white ${
        claim.has_badge ? "border-[#2E7D5B]" : "border-[#E5E1D8]"
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-display text-lg font-medium text-[#2A3B2C] line-clamp-1">
              {claim.restaurant_name || "Restaurant"}
            </h3>
            {claim.has_badge && (
              <span
                data-testid={`badge-active-${claim.place_id}`}
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-[#2E7D5B] text-white"
              >
                <BadgeCheck className="w-2.5 h-2.5" />
                Halal Verified
              </span>
            )}
            {claim.has_pending_edit && (
              <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-[#FFF3EE] text-[#D95D39]">
                Edit pending review
              </span>
            )}
          </div>
          <p className="text-xs text-[#647466] mt-1">
            Verified · keep your details fresh for diners
          </p>
        </div>
        <div className="flex flex-col gap-1.5 items-end shrink-0">
          {claim.has_badge ? <BadgeStatusPill claim={claim} token={token} /> : null}
          <button
            data-testid={`owner-edit-toggle-${claim.place_id}`}
            onClick={() => setOpen((o) => !o)}
            className="inline-flex items-center gap-1.5 h-9 px-3 rounded-full bg-white border border-[#E5E1D8] hover:border-[#2A3B2C] text-[#2A3B2C] text-xs font-medium"
          >
            <Pencil className="w-3 h-3" />
            {open ? "Close" : "Edit listing"}
          </button>
        </div>
      </div>

      {!claim.has_badge && (
        <div
          data-testid={`owner-verify-cta-${claim.place_id}`}
          className="mt-4 rounded-2xl border border-[#2E7D5B]/30 bg-[#F0F8F4] p-4 flex flex-col sm:flex-row sm:items-center gap-3"
        >
          <div className="w-10 h-10 rounded-xl bg-[#2E7D5B] flex items-center justify-center shrink-0">
            <BadgeCheck className="w-5 h-5 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <h4 className="font-display text-sm font-semibold text-[#1F5C42]">
              Verify your Halal Status here
            </h4>
            <p className="text-xs text-[#1F5C42]/80 mt-0.5 leading-relaxed">
              Pay <span className="font-semibold">$5/year</span> to display the
              official <span className="font-semibold">Halal Verified</span>{" "}
              badge on your listing. Diners trust badged restaurants 3× more,
              and you&apos;ll rank higher in halal-only filters.
            </p>
          </div>
          <BadgePurchaseButton
            claim={claim}
            token={token}
            label="Verify — $5/yr"
          />
        </div>
      )}

      {open && (
        <div className="mt-4 border-t border-[#E5E1D8] pt-4">
          {loading || !draft ? (
            <div className="flex items-center justify-center py-6">
              <Loader2 className="w-4 h-4 animate-spin text-[#D95D39]" />
            </div>
          ) : (
            <EditPanel
              placeId={claim.place_id}
              token={token}
              draft={draft}
              onChanged={() => {
                loadDraft();
                onChanged?.();
              }}
            />
          )}
        </div>
      )}
    </div>
  );
}

// ---------- Edit panel (fields + photo + menu uploaders) ----------
function EditPanel({ placeId, token, draft, onChanged }) {
  const merged = { ...draft.approved_fields, ...draft.pending_fields };
  const [phone, setPhone] = useState(merged.phone || "");
  const [website, setWebsite] = useState(merged.website || "");
  const [cuisine, setCuisine] = useState(merged.cuisine || "");
  const [description, setDescription] = useState(merged.description || "");
  const [halalStatus, setHalalStatus] = useState(merged.halal_status || "");
  const [halalCertBody, setHalalCertBody] = useState(
    merged.halal_certification_body || ""
  );
  const [halalCertNumber, setHalalCertNumber] = useState(
    merged.halal_certification_number || ""
  );
  const [hasAlcohol, setHasAlcohol] = useState(merged.halal_has_alcohol ?? null);
  // Halal preparation attributes (iter19) — surfaced on the public card.
  const [handSlaughtered, setHandSlaughtered] = useState(
    merged.halal_hand_slaughtered ?? null
  );
  const [separateCooking, setSeparateCooking] = useState(
    merged.halal_separate_cooking_area ?? null
  );
  const [halalOnRequest, setHalalOnRequest] = useState(
    merged.halal_on_request ?? null
  );
  const [saving, setSaving] = useState(false);

  const photoInputRef = useRef(null);
  const menuInputRef = useRef(null);

  const submitFields = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post(
        `/owner/restaurants/${placeId}/edit`,
        {
          phone: phone || null,
          website: website || null,
          cuisine: cuisine || null,
          description: description || null,
          halal_status: halalStatus || null,
          halal_certification_body:
            halalStatus === "certified" ? halalCertBody || null : null,
          halal_certification_number:
            halalStatus === "certified" ? halalCertNumber || null : null,
          halal_has_alcohol: hasAlcohol,
          halal_hand_slaughtered: handSlaughtered,
          halal_separate_cooking_area: separateCooking,
          halal_on_request: halalOnRequest,
        },
        { headers: { "X-Owner-Token": token } }
      );
      toast.success("Edit submitted for admin review");
      onChanged?.();
    } catch (err) {
      toast.error("Couldn't save edits", {
        description: err?.response?.data?.detail || err.message,
      });
    } finally {
      setSaving(false);
    }
  };

  const uploadFile = async (file, kind) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(
        `/owner/restaurants/${placeId}/${kind === "photo" ? "photos" : "menu"}`,
        fd,
        {
          headers: {
            "X-Owner-Token": token,
            // No manual Content-Type — axios appends the correct multipart
            // boundary automatically when the body is a FormData instance.
          },
        }
      );
      toast.success(
        `${kind === "photo" ? "Photo" : "Menu"} uploaded — queued for review`
      );
      onChanged?.();
    } catch (err) {
      toast.error(`Upload failed`, {
        description: err?.response?.data?.detail || err.message,
      });
    }
  };

  const deleteFile = async (fileId, kind) => {
    if (!window.confirm(`Remove this ${kind}?`)) return;
    try {
      await api.delete(
        `/owner/restaurants/${placeId}/${
          kind === "photo" ? "photos" : "menu"
        }/${fileId}`,
        { headers: { "X-Owner-Token": token } }
      );
      toast.success(`${kind === "photo" ? "Photo" : "Menu"} removed`);
      onChanged?.();
    } catch (err) {
      toast.error("Delete failed", { description: err.message });
    }
  };

  const allPhotos = [...draft.approved_photos, ...draft.pending_photos];
  const allMenus = [...draft.approved_menus, ...draft.pending_menus];

  return (
    <form className="space-y-4" onSubmit={submitFields}>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="Phone">
          <Input
            data-testid="owner-edit-phone"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="+61 ..."
            className="h-10 rounded-lg border-[#E5E1D8] focus-visible:ring-[#D95D39]"
          />
        </Field>
        <Field label="Website">
          <Input
            data-testid="owner-edit-website"
            value={website}
            onChange={(e) => setWebsite(e.target.value)}
            placeholder="https://..."
            className="h-10 rounded-lg border-[#E5E1D8] focus-visible:ring-[#D95D39]"
          />
        </Field>
        <Field label="Cuisine">
          <select
            data-testid="owner-edit-cuisine"
            value={cuisine}
            onChange={(e) => setCuisine(e.target.value)}
            className="h-10 w-full rounded-lg border border-[#E5E1D8] px-3 text-sm bg-white"
          >
            <option value="">— pick a cuisine —</option>
            {CUISINES.filter((c) => c.id !== "all").map((c) => (
              <option key={c.id} value={c.id}>
                {c.label}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Halal status">
          <select
            data-testid="owner-edit-halal-status"
            value={halalStatus}
            onChange={(e) => setHalalStatus(e.target.value)}
            className="h-10 w-full rounded-lg border border-[#E5E1D8] px-3 text-sm bg-white"
          >
            <option value="">— pick —</option>
            <option value="certified">Certified halal</option>
            <option value="self_declared">Self-declared halal</option>
            <option value="muslim_owned">Muslim-owned</option>
          </select>
        </Field>
      </div>
      {halalStatus === "certified" && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 rounded-xl bg-[#F0F8F4]/60 border border-[#2E7D5B]/20 p-3">
          <Field label="Certifying body">
            <Input
              data-testid="owner-edit-halal-cert-body"
              value={halalCertBody}
              onChange={(e) => setHalalCertBody(e.target.value)}
              placeholder="HFA UK, JAKIM, AFIC…"
              className="h-10 rounded-lg border-[#E5E1D8] focus-visible:ring-[#2E7D5B] bg-white"
            />
          </Field>
          <Field label="Certificate number">
            <Input
              data-testid="owner-edit-halal-cert-num"
              value={halalCertNumber}
              onChange={(e) => setHalalCertNumber(e.target.value)}
              placeholder="optional"
              className="h-10 rounded-lg border-[#E5E1D8] focus-visible:ring-[#2E7D5B] bg-white"
            />
          </Field>
        </div>
      )}
      <Field label="Description (max 1000 chars)">
        <textarea
          data-testid="owner-edit-description"
          value={description}
          onChange={(e) => setDescription(e.target.value.slice(0, 1000))}
          placeholder="Tell diners what makes your place special..."
          className="w-full min-h-[80px] rounded-lg border border-[#E5E1D8] px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#D95D39]"
        />
      </Field>
      <Field label="Serves alcohol?">
        <div className="flex gap-2">
          {[
            { v: false, l: "No alcohol" },
            { v: true, l: "Yes" },
          ].map((opt) => (
            <button
              key={String(opt.v)}
              type="button"
              data-testid={`owner-edit-alcohol-${opt.v ? "yes" : "no"}`}
              onClick={() => setHasAlcohol(opt.v)}
              className={`h-9 px-4 rounded-full text-xs font-medium border ${
                hasAlcohol === opt.v
                  ? "bg-[#2A3B2C] text-white border-transparent"
                  : "bg-white text-[#647466] border-[#E5E1D8]"
              }`}
            >
              {opt.l}
            </button>
          ))}
          {hasAlcohol !== null && (
            <button
              type="button"
              onClick={() => setHasAlcohol(null)}
              className="text-[10px] text-[#8A9A8B] underline"
            >
              clear
            </button>
          )}
        </div>
      </Field>

      {/* Halal preparation attributes — shown on the public restaurant card */}
      <div
        data-testid="owner-edit-halal-attrs"
        className="rounded-2xl border border-[#2E7D5B]/25 bg-[#F0F8F4]/60 p-4"
      >
        <div className="flex items-center gap-2 mb-2">
          <BadgeCheck className="w-4 h-4 text-[#2E7D5B]" />
          <h4 className="font-display text-sm font-medium text-[#1F5C42]">
            Halal preparation
          </h4>
        </div>
        <p className="text-[11px] text-[#1F5C42]/70 mb-3 leading-relaxed">
          Toggle anything that applies — these show up as badges on your card
          and help diners with specific halal preferences find you.
        </p>
        <div className="space-y-2">
          {[
            {
              key: "halal_hand_slaughtered",
              label: "Meat is hand slaughtered (zabihah)",
              state: handSlaughtered,
              set: setHandSlaughtered,
            },
            {
              key: "halal_separate_cooking_area",
              label: "Separate cooking area for halal meat",
              state: separateCooking,
              set: setSeparateCooking,
            },
            {
              key: "halal_on_request",
              label: "Halal meat available on request",
              state: halalOnRequest,
              set: setHalalOnRequest,
            },
          ].map((opt) => (
            <label
              key={opt.key}
              className="flex items-center gap-2 cursor-pointer text-xs text-[#2A3B2C]"
            >
              <input
                type="checkbox"
                data-testid={`owner-edit-${opt.key.replace(/_/g, "-")}`}
                checked={opt.state === true}
                onChange={(e) =>
                  opt.set(e.target.checked ? true : null)
                }
                className="w-4 h-4 rounded border-[#2E7D5B]/40 text-[#2E7D5B] focus:ring-[#2E7D5B]"
              />
              <span>{opt.label}</span>
            </label>
          ))}
        </div>
      </div>

      <button
        type="submit"
        disabled={saving}
        data-testid="owner-edit-submit"
        className="h-10 px-5 rounded-full bg-[#D95D39] hover:bg-[#C54E2C] text-white text-sm font-medium inline-flex items-center gap-2 disabled:opacity-50"
      >
        {saving ? (
          <Loader2 className="w-4 h-4 animate-spin" />
        ) : (
          <Pencil className="w-3.5 h-3.5" />
        )}
        Submit for review
      </button>

      {/* Photos */}
      <div className="pt-4 border-t border-[#E5E1D8]">
        <div className="flex items-center justify-between mb-2">
          <Label className="text-xs uppercase tracking-[0.18em] font-bold text-[#647466]">
            Photos ({allPhotos.length}/8)
          </Label>
          <button
            type="button"
            data-testid="owner-add-photo-btn"
            onClick={() => photoInputRef.current?.click()}
            className="inline-flex items-center gap-1 text-xs text-[#D95D39] hover:underline"
          >
            <ImagePlus className="w-3.5 h-3.5" />
            Add photo
          </button>
          <input
            ref={photoInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => uploadFile(e.target.files?.[0], "photo")}
          />
        </div>
        {allPhotos.length === 0 ? (
          <p className="text-xs text-[#8A9A8B] italic">
            No photos yet. Owner photos appear before Google&apos;s.
          </p>
        ) : (
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
            {allPhotos.map((p) => {
              const isPending = draft.pending_photos.some(
                (x) => x.file_id === p.file_id
              );
              return (
                <div
                  key={p.file_id}
                  className="relative aspect-square rounded-lg overflow-hidden border border-[#E5E1D8] bg-[#F2EDE3]"
                >
                  <img
                    src={`${process.env.REACT_APP_BACKEND_URL}/api/restaurants/${placeId}/photo/${p.file_id}`}
                    alt={p.filename}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.currentTarget.style.display = "none";
                    }}
                  />
                  {isPending && (
                    <span className="absolute top-1 left-1 px-1.5 py-0.5 rounded-full text-[8px] font-bold bg-[#FFF3EE] text-[#D95D39]">
                      PENDING
                    </span>
                  )}
                  <button
                    type="button"
                    onClick={() => deleteFile(p.file_id, "photo")}
                    className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/60 text-white inline-flex items-center justify-center"
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Menus */}
      <div className="pt-4 border-t border-[#E5E1D8]">
        <div className="flex items-center justify-between mb-2">
          <Label className="text-xs uppercase tracking-[0.18em] font-bold text-[#647466]">
            Menu files ({allMenus.length}/4)
          </Label>
          <button
            type="button"
            data-testid="owner-add-menu-btn"
            onClick={() => menuInputRef.current?.click()}
            className="inline-flex items-center gap-1 text-xs text-[#D95D39] hover:underline"
          >
            <FileText className="w-3.5 h-3.5" />
            Upload menu
          </button>
          <input
            ref={menuInputRef}
            type="file"
            accept="image/*,application/pdf"
            className="hidden"
            onChange={(e) => uploadFile(e.target.files?.[0], "menu")}
          />
        </div>
        {allMenus.length === 0 ? (
          <p className="text-xs text-[#8A9A8B] italic">
            Upload your menu — PDF or image. Diners can view it on your listing.
          </p>
        ) : (
          <ul className="space-y-1.5">
            {allMenus.map((m) => {
              const isPending = draft.pending_menus.some(
                (x) => x.file_id === m.file_id
              );
              return (
                <li
                  key={m.file_id}
                  className="flex items-center justify-between gap-2 rounded-lg border border-[#E5E1D8] px-3 py-2 text-xs"
                >
                  <span className="flex items-center gap-2 min-w-0">
                    <FileText className="w-3.5 h-3.5 text-[#8A9A8B] shrink-0" />
                    <span className="truncate text-[#2A3B2C]">{m.filename}</span>
                    {isPending && (
                      <span className="px-1.5 py-0.5 rounded-full text-[8px] font-bold bg-[#FFF3EE] text-[#D95D39]">
                        PENDING
                      </span>
                    )}
                  </span>
                  <button
                    type="button"
                    onClick={() => deleteFile(m.file_id, "menu")}
                    className="text-[#8A9A8B] hover:text-[#D95D39]"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </form>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-[0.18em] font-bold text-[#647466] mb-1 block">
        {label}
      </Label>
      {children}
    </div>
  );
}

// ---------- Badge purchase / renewal ----------
function BadgePurchaseButton({ claim, token, label }) {
  const [buying, setBuying] = useState(false);

  const handleBuy = async () => {
    setBuying(true);
    try {
      const res = await api.post(
        "/owner/badge/checkout",
        { claim_id: claim.claim_id, origin_url: window.location.origin },
        { headers: { "X-Owner-Token": token } }
      );
      track(EVENTS.BADGE_CHECKOUT_STARTED, { place_id: claim.place_id });
      window.location.href = res.data.url;
    } catch (err) {
      toast.error("Badge checkout failed", {
        description: err?.response?.data?.detail || err.message,
      });
      setBuying(false);
    }
  };

  return (
    <button
      data-testid={`owner-buy-badge-${claim.place_id}`}
      onClick={handleBuy}
      disabled={buying}
      className="inline-flex items-center gap-1.5 h-9 px-3 rounded-full bg-[#2E7D5B] hover:bg-[#256249] text-white text-xs font-medium disabled:opacity-50"
      title="Buy or renew a $5/year Halal Verified trust badge"
    >
      {buying ? (
        <Loader2 className="w-3.5 h-3.5 animate-spin" />
      ) : (
        <>
          <BadgeCheck className="w-3.5 h-3.5" />
          {label || "Get Verified — $5/yr"}
        </>
      )}
    </button>
  );
}

function BadgeStatusPill({ claim, token }) {
  const days = claim.badge_days_remaining ?? null;
  const dueSoon = days !== null && days <= 30;
  return (
    <div className="flex flex-col items-end gap-1">
      <span
        data-testid={`badge-status-${claim.place_id}`}
        className={`inline-flex items-center gap-1.5 h-7 px-2.5 rounded-full text-[10px] font-bold ${
          dueSoon ? "bg-[#FFF3EE] text-[#D95D39]" : "bg-[#2E7D5B] text-white"
        }`}
        title={
          claim.badge_expires_at
            ? `Expires ${new Date(claim.badge_expires_at).toLocaleDateString()}`
            : "Active"
        }
      >
        <BadgeCheck className="w-3 h-3" />
        {days !== null ? `${days}d left` : "Active"}
      </span>
      {dueSoon && (
        <BadgePurchaseButton claim={claim} token={token} label={`Renew — $5`} />
      )}
    </div>
  );
}
